<template>
  <v-row id="pricing" class="pricing-section py-12" no-gutters>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="top"
      class="text-center"
    >
      {{ snackbar.text }}
    </v-snackbar>
    
    <v-container>
      <v-row>
        <v-col cols="12" class="text-center mb-8">
          <h2 class="typography-h3 weight-bold">{{ $t('pricing.title') }}</h2>
          <p class="typography-subtitle-1 text-muted mt-2">{{ $t('pricing.subtitle') }}</p>
        </v-col>
      </v-row>

      <v-row v-if="pricingError" justify="center" class="mb-4">
        <v-col cols="12" md="10" lg="8">
          <v-alert
            type="info"
            variant="tonal"
            class="pricing-fallback-note"
          >
            {{ $t('pricing.fallbackNotice') }}
          </v-alert>
        </v-col>
      </v-row>

      <v-row justify="center" class="mt-4">
        <v-col
          v-for="(plan, index) in plans"
          :key="index"
          cols="12"
          sm="10"
          md="6"
          lg="5"
          class="d-flex"
        >
          <v-card 
            class="price-card d-flex flex-column"
            :class="[plan.popular ? 'card-action' : 'card-standard', { popular: plan.popular }]"
            :data-popular-text="$t('pricing.popular_badge')"
            elevation="2"
            width="100%"
          >
            <v-card-item class="card-content">
              <div class="text-center">
                <h3 class="typography-h5 weight-bold mb-4">{{ $t(plan.nameKey) }}</h3>
                <div class="price-container mb-2">
                  <template v-if="plan.originalPrice">
                    <span class="original-price typography-body-lg weight-medium text-decoration-line-through mr-2">{{ plan.originalPrice }}</span>
                    <span class="typography-h3 weight-bold">{{ plan.price }}</span>
                  </template>
                  <template v-else>
                    <span class="typography-h3 weight-bold">{{ plan.price }}</span>
                  </template>
                  <span class="typography-subtitle-1 weight-medium ml-1">{{ $t('pricing.currency') }}</span>
                </div>
                <div class="typography-body text-medium-emphasis mb-2">{{ $t(plan.billingKey, { credits: plan.credits }) }}</div>
                <div v-if="plan.valueNoteKey" class="pricing-value-note">
                  {{ $t(plan.valueNoteKey, { credits: plan.credits }) }}
                </div>
              </div>

              <v-divider class="mb-6"></v-divider>

              <v-list 
                density="compact" 
                class="bg-transparent pa-0"
              >
                <v-list-item
                  v-for="(feature, fIndex) in plan.features"
                  :key="fIndex"
                  class="px-0 mb-1"
                  density="compact"
                >
                  <template v-slot:prepend>
                    <v-icon 
                      :color="feature.included ? 'success' : 'grey'" 
                      size="small" 
                      class="mr-2"
                    >
                      {{ feature.included ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                  </template>
                  <v-list-item-title
                    :class="['feature-text', { 'text-medium-emphasis': !feature.included }]"
                  >
                    {{ $t(feature.textKey, { credits: plan.credits }) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-item>

            <v-card-actions class="mt-auto pa-4">
              <v-btn
                block
                :color="plan.popular ? 'primary' : 'default'"
                :variant="plan.popular ? 'flat' : 'outlined'"
                size="large"
                @click="handlePlanAction(plan)"
              >
                {{ $t(plan.buttonTextKey) }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <PayPalButton
      v-model="showPayPal"
      @payment-success="handlePaymentSuccess"
      @payment-error="handlePaymentError"
    />
  </v-row>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import PayPalButton from '@/components/common/PayPalButton.vue';
import { getIdToken } from 'firebase/auth';
import { useRouter, useRoute } from 'vue-router';
import { trackEvent } from '@/utils/analytics';

const { t, locale } = useI18n();

const showPayPal = ref(false);
const authStore = useAuthStore();
const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
});

const pricingConfig = ref(null);
const pricingError = ref(false);
const router = useRouter();
const route = useRoute();
const pricingIntentHandled = ref(false);

// Add watch for auth state changes
watch(
  () => authStore.user,
  async () => {
    try {
      let headers = {};
      if (authStore.isAuthenticated) {
        const token = await getIdToken(authStore.user);
        headers.Authorization = `Bearer ${token}`;
      }

      pricingConfig.value = await fetchPricingConfig(headers);
      initializePlans();
      await handlePricingIntent();
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      useFallbackPricing();
    }
  }
);

onMounted(async () => {
  try {
    let headers = {};
    if (authStore.isAuthenticated) {
      const token = await getIdToken(authStore.user);
      headers.Authorization = `Bearer ${token}`;
    }

    pricingConfig.value = await fetchPricingConfig(headers);
    initializePlans();
    await handlePricingIntent();
  } catch (error) {
    console.error('Failed to fetch pricing:', error);
    useFallbackPricing();
  }
});

const fetchPricingConfig = async (headers = {}) => {
  const response = await fetch(`/api/pricing?locale=${locale.value}`, { headers });
  const contentType = response.headers.get('content-type') || '';

  if (!response.ok || !contentType.includes('application/json')) {
    const body = await response.text();
    throw new Error(`Pricing endpoint unavailable (${response.status}): ${body.slice(0, 120)}`);
  }

  const config = await response.json();
  if (
    typeof config?.discountedPrice !== 'number' ||
    typeof config?.regularPrice !== 'number' ||
    typeof config?.credits !== 'number'
  ) {
    throw new Error('Pricing endpoint returned an invalid payload');
  }

  pricingError.value = false;
  return config;
};

const useFallbackPricing = () => {
  pricingError.value = true;
  pricingConfig.value = {
    credits: 5,
    regularPrice: 9.99,
    discountedPrice: 3.99,
    currency: locale.value === 'en' ? 'USD' : 'EUR',
  };
  initializePlans();
};

const handlePricingIntent = async () => {
  const wantsPricing = route.query.pricing === '1' || route.query.redirect === 'pricing';
  if (!wantsPricing || pricingIntentHandled.value) {
    return;
  }

  pricingIntentHandled.value = true;
  await nextTick();

  const pricingElement = document.getElementById('pricing');
  if (pricingElement) {
    pricingElement.scrollIntoView({ behavior: 'smooth' });
  }

  if (route.query.buyCredits === '1' && authStore.isAuthenticated) {
    showPayPal.value = true;
  }

  const nextQuery = { ...route.query };
  delete nextQuery.pricing;
  delete nextQuery.redirect;
  delete nextQuery.buyCredits;

  await router.replace({
    path: route.path,
    query: nextQuery,
  });
};

const initializePlans = () => {
  if (!pricingConfig.value) return;

  plans.value = [
    {
      nameKey: 'pricing.plans.basic.name',
      price: '0',
      billingKey: 'pricing.free',
      buttonTextKey: 'pricing.start_now',
      features: [
        { textKey: 'pricing.plans.basic.features.free_analysis', included: true },
        { textKey: 'pricing.plans.basic.features.basic_ai', included: true },
        { textKey: 'pricing.plans.basic.features.basic_recommendations', included: true },
      ]
    },
    {
      nameKey: 'pricing.plans.credits.name',
      price: pricingConfig.value.discountedPrice.toFixed(2).replace('.', ','),
      originalPrice: pricingConfig.value.regularPrice.toFixed(2).replace('.', ','),
      billingKey: 'pricing.plans.credits.billing',
      valueNoteKey: 'pricing.plans.credits.valueNote',
      popular: true,
      buttonTextKey: 'pricing.buy_credits',
      credits: pricingConfig.value.credits,
      features: [
        { textKey: 'pricing.plans.credits.features.included_analysis', included: true },
        { textKey: 'pricing.plans.credits.features.advanced_ai', included: true },      
        { textKey: 'pricing.plans.basic.features.detailed_recommendations', included: true },
        { textKey: 'pricing.plans.credits.features.cost_efficiency', included: true },
        { textKey: 'pricing.plans.credits.features.room_analysis', included: true },
        { textKey: 'pricing.plans.basic.features.export', included: true },
        { textKey: 'pricing.plans.basic.features.support', included: true }
      ]
    }
  ];
};

const handlePlanAction = (plan) => {
  if (plan.price === '0') {
    scrollToTop();
    return;
  }
  
  if (!authStore.isAuthenticated) {
    router.push({
      path: '/sign-up',
      query: {
        redirect: 'pricing',
        buyCredits: '1',
      }
    });
    return;
  }
  
  // Track PayPal button click
  trackEvent('paypal_button_click', {
    plan_type: plan.nameKey,
    price: plan.price,
    credits: plan.credits
  });
  showPayPal.value = true;
};

const handlePaymentSuccess = () => {
  showNotification(t('pricing.payment.success'), 'success');
  authStore.fetchCredits();
};

const handlePaymentError = (error) => {
  console.error('Payment failed:', error);
  showNotification(t('pricing.payment.error'), 'error');
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

const showNotification = (text, color = 'success') => {
  snackbar.value = {
    show: true,
    text,
    color
  };
};

const plans = ref([]);
</script>

<style scoped>
.pricing-section {
  padding: 4rem 0;
  background: var(--background);
}

@media (max-width: 600px) {
  .pricing-section {
    padding: 2rem 0;
  }
}

.price-card {
  height: 100%;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 1rem;
}

.card-content {
  padding: 1rem;
}

.price-card.popular {
  border: 2px solid var(--primary);
  position: relative;
}

.price-card.popular::before {
  content: attr(data-popular-text);
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--primary);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
}

.price-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  color: var(--primary);
}

.popular .price-container {
  color: var(--primary-light);
}

.text-medium-emphasis {
  color: var(--text-secondary) !important;
}

.pricing-fallback-note {
  border-radius: 8px;
}

.pricing-value-note {
  max-width: 280px;
  margin: 0 auto 20px;
  color: var(--text-secondary);
  font-size: var(--font-size-body-2);
  line-height: 1.45;
}

.feature-text {
  font-size: var(--font-size-body-1);
  line-height: var(--line-height-relaxed);
  color: var(--text-primary);
  white-space: normal;
}

.paypal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.paypal-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  position: relative;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
}

.paypal-buttons-container {
  display: none;
}

.original-price {
  color: var(--text-secondary);
  font-size: var(--font-size-body-lg);
  opacity: 0.75;
}

@media (max-width: 600px) {
  .price-card {
    padding: 0.5rem;
  }
  
  .card-content {
    padding: 0.5rem;
  }

  .v-card-actions {
    padding: 0.5rem !important;
  }

  .mb-6 {
    margin-bottom: 1rem !important;
  }

  .mb-4 {
    margin-bottom: 0.5rem !important;
  }
}
</style>
